/**
 * QuickActionsSection Component
 * Quick action buttons for common contact operations
 * Includes placeholders for Zuora integration (address/payment changes)
 */

import React from "react";
import { Link } from "react-router-dom";
import { useToast } from "../ui/Toast";
import type { Contact } from "../../types/contact.types";
import { isFeatureEnabled } from "../../utils/featureFlags";

interface QuickActionsSectionProps {
  contact: Contact;
  onSendSMS?: () => void;
  onSendEmail?: () => void;
  onStartSequence?: () => void;
}

const QuickActionsSection: React.FC<QuickActionsSectionProps> = ({
  contact,
  onSendSMS,
  onSendEmail,
  onStartSequence,
}) => {
  const { showToast } = useToast();

  const handleChangeAddress = () => {
    // Placeholder for Zuora integration
    showToast("Address change feature coming soon (Zuora integration)", "info");
  };

  const handleChangePaymentMethod = () => {
    // Placeholder for Zuora integration
    showToast(
      "Payment method change feature coming soon (Zuora integration)",
      "info",
    );
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 mb-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        Quick Actions
      </h3>
      <div className="flex flex-wrap gap-2">
        {/* Communication Actions */}
        {isFeatureEnabled("operatorSms") &&
          contact.phone_number &&
          onSendSMS && (
            <button
              onClick={onSendSMS}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              Send SMS
            </button>
          )}

        {contact.email && onSendEmail && (
          <button
            onClick={onSendEmail}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Send Email
          </button>
        )}

        {/* Start a sequence: manually enroll THIS contact into an existing
            active message sequence (post-support follow-ups). Does not change
            the sequence's own criteria. */}
        {onStartSequence && (
          <button
            onClick={onStartSequence}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Start a Sequence
          </button>
        )}

        {/* R7a: Log a phone sale tied to this contact. Standalone create
            page prefills + locks the contact via ?contact_id=. */}
        <Link
          to={`/sales-activities/phone-sales/new?contact_id=${contact.id}`}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          Log Phone Sale
        </Link>

        {/* Zuora Integration Placeholders */}
        <button
          onClick={handleChangeAddress}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          title="Coming soon - Zuora integration"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Change Address
          <span className="text-xs text-gray-400">(Soon)</span>
        </button>

        <button
          onClick={handleChangePaymentMethod}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          title="Coming soon - Zuora integration"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
          Change Payment
          <span className="text-xs text-gray-400">(Soon)</span>
        </button>
      </div>
    </div>
  );
};

export default QuickActionsSection;
